/**
 * Preview Tab Sections Configuration
 * 
 * Centralized configuration for admin preview tab sections.
 * Mirrors the provider details sections but for the admin context.
 * 
 * Each section declares:
 * - id: unique identifier for tabs
 * - label: display name
 * - hasData: validation function to determine if section should render
 */

import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import {
  hasOverviewData,
  hasServicesData,
  hasQualificationsData,
  hasMediaData,
  hasReviewsData,
} from '@/features/provider-directory/utils/fieldValidation';

export type PreviewSection = {
  /** Unique identifier for the section/tab */
  id: string;
  
  /** Display label for the tab */
  label: string;
  
  /** Function to determine if section has data to display */
  hasData: (provider: ProviderProfile) => boolean;
  
  /** Order for display (lower numbers appear first) */
  order: number;
};

/**
 * Get available sections for preview tab
 * Sections are returned in display order
 *
 * IMPORTANT: Must match the sections in getProviderDetailsSections (provider-directory/utils/getSections.ts)
 * to ensure admin preview matches actual public profile.
 *
 * @param provider - Transformed provider profile from form data
 * @returns Array of section configurations that have data
 */
export const getPreviewSections = (
  provider: ProviderProfile
): PreviewSection[] => {
  const sections: PreviewSection[] = [
    {
      id: 'overview',
      label: 'Overview',
      hasData: (p) => hasOverviewData(p),
      order: 1,
    },
    {
      id: 'services',
      label: 'Services',
      hasData: (p) => hasServicesData(p),
      order: 2,
    },
    {
      id: 'care-delivery',
      label: 'Care Delivery',
      hasData: (p) => {
        // Feature 038: Show Care Delivery tab if provider has any care delivery information
        return Boolean(
          p.priceDetailsText ||
          p.carePhilosophy ||
          p.emergencyResponseDetails
        );
      },
      order: 2.5,
    },
    {
      id: 'qualifications',
      label: 'Qualifications',
      hasData: (p) => hasQualificationsData(p),
      order: 3,
    },
    {
      id: 'resources',
      label: 'Resources',
      hasData: (p) => {
        // Feature 011 - US1: Show Resources tab if provider has authored resources OR photos
        const hasPhotos = hasMediaData(p);
        const contributedResources = (p as any).contributedResources as unknown[] | undefined;
        const hasResources = Array.isArray(contributedResources) && contributedResources.length > 0;
        return hasPhotos || hasResources;
      },
      order: 4,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      hasData: (p) => hasReviewsData(p),
      order: 5,
    },
  ];

  // Filter sections that have data and sort by order
  return sections
    .filter(section => section.hasData(provider))
    .sort((a, b) => a.order - b.order);
};

/**
 * Get the default active tab (first available section)
 */
export const getDefaultPreviewTab = (sections: PreviewSection[]): string => {
  return sections[0]?.id || 'overview';
};

